/**
 * GZ#4977 — create and edit must send the same private-match billing plan.
 *
 * Create already writes open_match_options.payment_split_mode. The edit PUT
 * used to omit it, so paymentSplitPlanForPrivateMatchUpdate kept the stored
 * plan (NULL = host pays full court). GZ#4695 then applied the picked mode
 * after save, but only when splitTarget was set and the PUT id matched
 * events.id — a silent no-op when the staff change never reached apply.
 *
 * This helper is the single builder for both paths. Edit also still PATCHes
 * /payment-split after a successful PUT so unpaid seats reprice the same way
 * as create. Paid seats are the apply endpoint's problem; we do not re-price
 * them here.
 */
import { openMatchLevelPayload } from "./openMatchLevelDefaults";
import { privateMatchFlagForPayload } from "./privateMatchSelection";
import type { SwitchableSplitMode } from "src/components/Calendar/paymentSplitPreview";

export type OpenMatchOptionsInput = {
  selectedOpenRegular: string;
  privateMatchToggle: boolean;
  /** Padel-style Private Match | Open Match tabs (not Regular + checkbox). */
  matchTypeTabs?: boolean;
  approve_before_join: boolean;
  friendly_match: boolean;
  submit_to_dupr: boolean;
  only_dupr_plus?: boolean;
  only_verified_users?: boolean;
  min_level: string;
  max_level: string;
  gender_preference?: string | null;
  /** Stored or create-time plan. Edit prefers splitTarget when set. */
  paymentSplitMode?: string;
  splitTarget?: SwitchableSplitMode | null;
  /**
   * GZ#5045: the raw stored payment_split_mode (NULL for app pay-my-share
   * bookings). Only read when isEditing is true.
   */
  storedSplitMode?: string | null;
  isEditing?: boolean;
  organizerBilledShares?: number;
};

/** Axios / MCP bodies are either { data: payload } or the payload itself. */
export function unwrapCalendarPayload<T = any>(axiosOrBody: any): T {
  const body = axiosOrBody && typeof axiosOrBody === "object" && "data" in axiosOrBody
    ? axiosOrBody.data
    : axiosOrBody;
  if (body && typeof body === "object" && body.data != null && typeof body.data === "object") {
    return body.data as T;
  }
  return body as T;
}

/** Calendar row: id and service_id are both the service booking id after flatten. */
export function resolveServiceBookingId(
  events: { id?: unknown; service_id?: unknown } | null | undefined
): number | null {
  for (const raw of [events?.id, events?.service_id]) {
    const id = Number(raw);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

export function paymentSplitModeForSave({
  selectedOpenRegular,
  paymentSplitMode,
  splitTarget,
  storedSplitMode,
  isEditing,
}: {
  selectedOpenRegular: string;
  paymentSplitMode?: string;
  splitTarget?: SwitchableSplitMode | null;
  storedSplitMode?: string | null;
  isEditing?: boolean;
}): string | undefined {
  if (selectedOpenRegular !== "Private Match") return undefined;
  // GZ#5045: on edit, an untouched switch keeps the stored plan. Sending the
  // modal's full_court default for a NULL plan made app joiners owe 0.
  if (isEditing) return splitTarget || storedSplitMode || undefined;
  return splitTarget || paymentSplitMode || "full_court";
}

export function buildOpenMatchOptions(
  input: OpenMatchOptionsInput
): Record<string, unknown> | undefined {
  if (
    input.selectedOpenRegular !== "Open Match" &&
    input.selectedOpenRegular !== "Private Match"
  ) {
    return undefined;
  }

  const payment_split_mode = paymentSplitModeForSave(input);
  return {
    private_match: privateMatchFlagForPayload(
      input.selectedOpenRegular,
      input.privateMatchToggle,
      { matchTypeTabs: input.matchTypeTabs }
    ),
    approve_before_join: input.approve_before_join,
    friendly_match: input.friendly_match,
    submit_to_dupr: input.submit_to_dupr,
    only_dupr_plus: input.only_dupr_plus,
    only_verified_users: input.only_verified_users,
    ...openMatchLevelPayload(input.min_level, input.max_level),
    gender_preference: input.gender_preference || null,
    ...(payment_split_mode
      ? {
          payment_split_mode,
          ...(payment_split_mode === "share_plus_others" && {
            organizer_billed_shares: Number(input.organizerBilledShares) || 1,
          }),
        }
      : {}),
  };
}

export type PaymentSplitApplyPlan =
  | { apply: true; id: number; mode: SwitchableSplitMode }
  | { apply: false; reason: "no-change" | "not-private" | "missing-id" };

/**
 * After the booking PUT, apply the picked split on the service booking id.
 * Do not require the PUT id to equal events.id — those names are not always
 * the same when the modal is opened from Payments / Bookings.
 */
export function paymentSplitApplyAfterSave({
  splitTarget,
  selectedOpenRegular,
  serviceBookingId,
  lessonSplit = false,
}: {
  splitTarget: SwitchableSplitMode | null | undefined;
  selectedOpenRegular: string;
  serviceBookingId: number | null | undefined;
  /** GZ#4955: a lesson on a club with the lesson payment split. */
  lessonSplit?: boolean;
}): PaymentSplitApplyPlan {
  if (!splitTarget) return { apply: false, reason: "no-change" };
  if (selectedOpenRegular !== "Private Match" && !lessonSplit) {
    return { apply: false, reason: "not-private" };
  }
  const id = Number(serviceBookingId);
  if (!Number.isFinite(id) || id <= 0) {
    return { apply: false, reason: "missing-id" };
  }
  return { apply: true, id, mode: splitTarget };
}

/** When staff pick Private Match / Open Match on a tab sport, sync the leftover checkbox. */
export function privateMatchFormValueForTab(
  tab: string,
  matchTypeTabs: boolean
): boolean | undefined {
  if (!matchTypeTabs) return undefined;
  return tab === "Private Match";
}
